import { useEffect, useMemo, useState } from 'react'
import { notebookLabel } from './notebookLabel'
import { flattenVisible } from './notebookTree'
import './NotebookNav.css'

const IconForward = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

const IconPlus = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const IconEllipsis = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="5" cy="12" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
  </svg>
)

// Sidebar list for notebook pages. Click a row to open it. The forward arrow
// drills into that page's nested pages, the same way the code library drills
// into a standard. Plus creates a nested page; the ellipsis opens rename /
// archive / delete.
function NotebookList({
  pages,
  flat = false,
  selectedId,
  onSelect,
  onNewChild,
  onRename,
  onArchive,
  onDelete,
  burrowId,
  onBurrow,
  error,
}) {
  const [menuId, setMenuId] = useState(null)

  useEffect(() => {
    if (!menuId) return undefined
    const close = () => setMenuId(null)
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [menuId])

  const rows = useMemo(() => {
    if (!pages) return []
    // Filtered view: a flat list of matching pages (no tree/indent).
    if (flat) return pages.map((page) => ({ page, depth: 0, hasChildren: false }))
    return flattenVisible(pages, new Set(), burrowId)
  }, [pages, flat, burrowId])

  if (error) return <div className="nav-table-status">{error}</div>
  if (!pages) return <div className="nav-table-status">Loading…</div>
  if (!pages.length) {
    return (
      <div className="nav-table-status">
        {flat ? 'No pages match these topics.' : 'No notes yet. Create one to get started.'}
      </div>
    )
  }
  if (!flat && burrowId && rows.length === 0) {
    return <div className="nav-table-status">Nothing here.</div>
  }

  return (
    <div className="nb-nav" role="tree" aria-label="Notes">
      {rows.map(({ page, hasChildren }) => {
        const id = String(page._id)
        const selected = id === selectedId
        return (
          <div
            key={id}
            className={'nb-nav-row' + (selected ? ' is-selected' : '')}
            role="treeitem"
            onClick={() => onSelect?.(page)}
          >
            <div className="nb-nav-label" title={notebookLabel(page)}>
              <span>{notebookLabel(page)}</span>
            </div>
            <div className="nb-nav-hover-actions">
              <button
                type="button"
                className="nb-nav-icon-btn"
                title="New nested page"
                aria-label="New nested page"
                onClick={(event) => {
                  event.stopPropagation()
                  onNewChild?.(page)
                }}
              >
                <IconPlus />
              </button>
              <div className="nb-nav-more">
                <button
                  type="button"
                  className={'nb-nav-icon-btn' + (menuId === id ? ' is-on' : '')}
                  title="More"
                  aria-label="More"
                  aria-haspopup="menu"
                  aria-expanded={menuId === id}
                  onClick={(event) => {
                    event.stopPropagation()
                    setMenuId((current) => (current === id ? null : id))
                  }}
                >
                  <IconEllipsis />
                </button>
                {menuId === id && (
                  <div
                    className="nb-nav-menu"
                    role="menu"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuId(null)
                        onRename?.(page)
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuId(null)
                        onArchive?.(page)
                      }}
                    >
                      Archive
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="is-danger"
                      onClick={() => {
                        setMenuId(null)
                        onDelete?.(page)
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="nb-nav-forward">
              {hasChildren && !flat && (
                <button
                  type="button"
                  className="nb-nav-icon-btn"
                  title="See nested pages"
                  aria-label="See nested pages"
                  onClick={(event) => {
                    event.stopPropagation()
                    onBurrow?.(page)
                  }}
                >
                  <IconForward />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default NotebookList
